import {useState} from 'react'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {useTranslation} from '@/translations/i18n'
import {Button} from '@/components/ui/button/Button'
import {Text} from '@/components/ui/text/Text'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {MailerFormValidationSchema, SendMailPayload} from '@/features/mailer/types'
import {MailerRecipientSection} from '@/features/mailer/components/mailer-recipient-section/MailerRecipientSection'
import {MailerComposeSection} from '@/features/mailer/components/mailer-compose-section/MailerComposeSection'
import {MailerRecipientsPreviewModal} from '@/features/mailer/components/mailer-recipients-preview-modal/MailerRecipientsPreviewModal'
import {MailerSendConfirmationDialog} from '@/features/mailer/components/mailer-send-confirmation-dialog/MailerSendConfirmationDialog'
import {useSendMail} from '@/features/mailer/queries/useSendMail'
import {useMailerLiveCount} from '@/features/mailer/hooks/useMailerLiveCount'
import {buildRecipientsPayload, plainTextBodyToHtml} from '@/features/mailer/utils'
import {errorHandler} from '@/utilities/helpers'
import {toast} from '@/utilities/toast/toast'
import {usePermissions, useAuthStore} from '@/features/auth/store/store'
import {StyledMailerFormContent, StyledMailerFooter} from '@/pages/mailer/style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Badge} from '@/components/ui/badge/Badge'
import {SearchSmIcon} from '@/components/ui/icon/Icon'

export const MailerForm = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const currentUser = useAuthStore(state => state.user)
    const [attachments, setAttachments] = useState<File[]>([])
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)
    const [isConfirmOpen, setIsConfirmOpen] = useState(false)
    const [previewRecipients, setPreviewRecipients] = useState<SendMailPayload['recipients'] | null>(null)

    const form = useForm<MailerFormValidationSchema>({
        resolver: zodResolver(MailerFormValidationSchema),
        defaultValues: {
            subject: '',
            body: '',
            includePatients: false,
            includeConsultants: false,
            patientMinAge: undefined,
            patientNeuromuscularDiagnosisId: undefined,
            patientServiceType: undefined,
            consultantSpecializationId: undefined,
            consultantStatus: undefined,
            consultantContractType: undefined
        }
    })

    const liveCount = useMailerLiveCount(form)
    const includePatients = form.watch('includePatients')
    const includeConsultants = form.watch('includeConsultants')
    const showLiveCount = includePatients || includeConsultants

    const sendMutation = useSendMail({
        onError: errorHandler,
        onSuccess: data => {
            toast.success(t('mailer:send_success', {count: data.recipientCount}))
            form.reset()
            setAttachments([])
            setIsConfirmOpen(false)
        }
    })

    const handlePreview = () => {
        const formData = form.getValues()

        if (!formData.includePatients && !formData.includeConsultants) {
            form.trigger('includePatients')
            return
        }

        setPreviewRecipients(buildRecipientsPayload(formData))
        setIsPreviewOpen(true)
    }

    const handleSendClick = () => {
        // DEV ONLY: skip recipients validation since the override redirects to a fixed test address.
        if (import.meta.env.DEV) {
            const {subject, body} = form.getValues()
            if (!subject || !body) {
                form.trigger(['subject', 'body'])
                return
            }
            setIsConfirmOpen(true)
            return
        }
        form.handleSubmit(() => {
            if (liveCount.total === 0) {
                toast.info(t('mailer:no_recipients_warning'))
                return
            }
            setIsConfirmOpen(true)
        })()
    }

    const handleSendTestToSelf = () => {
        if (!currentUser?.email) return
        const {subject, body} = form.getValues()
        if (!subject || !body) {
            form.trigger(['subject', 'body'])
            return
        }
        // DEV override (handleConfirmSend) prevails for live invio. Test-send bypasses confirm dialog
        // and targets the operator's own email regardless of preview/filters.
        sendMutation.mutate(
            {
                payload: {
                    subject: `[ANTEPRIMA] ${subject}`,
                    body: plainTextBodyToHtml(body),
                    recipients: {
                        consultants: {
                            filters: {
                                operator: 'and' as const,
                                fields: [{key: 'email', operator: '=', value: currentUser.email}]
                            }
                        }
                    }
                },
                attachments
            },
            {
                onSuccess: () => toast.success(t('mailer:send_test_to_self_success', {email: currentUser.email}))
            }
        )
    }

    const handleConfirmSend = () => {
        const formData = form.getValues()
        const realRecipients = buildRecipientsPayload(formData)

        // DEV ONLY: redirect every email to a single test address while iterating locally.
        const recipients = import.meta.env.DEV
            ? {
                  consultants: {
                      filters: {
                          operator: 'and' as const,
                          fields: [{key: 'email', operator: '=', value: 'luca.denni@humans.tech'}]
                      }
                  }
              }
            : realRecipients

        sendMutation.mutate({
            payload: {
                subject: formData.subject,
                body: plainTextBodyToHtml(formData.body),
                recipients
            },
            attachments
        })
    }

    const handleReset = () => {
        form.reset()
        setAttachments([])
        setPreviewRecipients(null)
    }

    return (
        <>
            <StyledMailerFormContent>
                <MailerRecipientSection form={form} />

                {showLiveCount && (
                    <Flexbox align="center" gap={2}>
                        <Button type="button" variant="ghost" size="sm" onClick={handlePreview}>
                            <SearchSmIcon />
                            {t('mailer:view_recipients_list')}
                        </Button>
                        <Badge variant={liveCount.total > 0 ? 'primary' : 'orange'}>
                            {liveCount.isLoading ? (
                                <Spinner size={12} />
                            ) : liveCount.isError ? (
                                t('mailer:recipients_count_error')
                            ) : (
                                t('mailer:recipients_count', {count: liveCount.total})
                            )}
                        </Badge>
                    </Flexbox>
                )}

                <MailerComposeSection form={form} attachments={attachments} onAttachmentsChange={setAttachments} />
            </StyledMailerFormContent>

            <StyledMailerFooter>
                <Button type="button" variant="ghost" onClick={handleReset}>
                    <Text size="textMd">{t('mailer:reset')}</Text>
                </Button>
                {permissions?.settings?.write && (
                    <>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleSendTestToSelf}
                            disabled={sendMutation.isPending || !currentUser?.email}
                        >
                            <Text size="textMd">{t('mailer:send_test_to_self')}</Text>
                        </Button>
                        <Button
                            type="button"
                            variant="primaryNeutral"
                            onClick={handleSendClick}
                            disabled={sendMutation.isPending}
                        >
                            <Text size="textMd">{sendMutation.isPending ? t('mailer:sending') : t('mailer:send')}</Text>
                            {sendMutation.isPending && <Spinner />}
                        </Button>
                    </>
                )}
            </StyledMailerFooter>

            {isPreviewOpen && previewRecipients && (
                <MailerRecipientsPreviewModal recipients={previewRecipients} onClose={() => setIsPreviewOpen(false)} />
            )}

            {isConfirmOpen && (
                <MailerSendConfirmationDialog
                    onClose={() => setIsConfirmOpen(false)}
                    onConfirm={handleConfirmSend}
                    isSending={sendMutation.isPending}
                    subject={form.watch('subject')}
                    attachments={attachments}
                    patientsCount={liveCount.patients}
                    consultantsCount={liveCount.consultants}
                    includePatients={includePatients}
                    includeConsultants={includeConsultants}
                    hasPatientFilters={
                        !!(
                            form.watch('patientMinAge') ||
                            form.watch('patientMaxAge') ||
                            form.watch('patientGender') ||
                            form.watch('patientNeuromuscularDiagnosisId') ||
                            form.watch('patientResidenceAslDistrictId') ||
                            form.watch('patientOutsideRegion') ||
                            form.watch('patientServiceType')
                        )
                    }
                    hasConsultantFilters={
                        !!(
                            form.watch('consultantSpecializationId') ||
                            form.watch('consultantStatus') ||
                            form.watch('consultantContractType') ||
                            form.watch('consultantWorkSector')
                        )
                    }
                />
            )}
        </>
    )
}
